import React, { useEffect, useState } from "react";
import { adminAPI } from "../../services/api/admin";

// Manually provision an account, mirroring the two in-app flows:
//   "new"      → fresh owner user + new billing account on a chosen plan (signup)
//   "existing" → a workspace inside an existing billing account (no new user)
export default function AdminProvisionPanel({ accounts = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new");
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    owner_name: "",
    owner_email: "",
    workspace_name: "",
    plan_id: "",
    billing_account_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open || plans.length) return;
    adminAPI.getPlans().then(setPlans).catch(() => setPlans([]));
  }, [open, plans.length]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg(null);
    try {
      const attrs =
        mode === "new"
          ? {
              owner_name: form.owner_name,
              owner_email: form.owner_email,
              workspace_name: form.workspace_name,
              plan_id: form.plan_id,
            }
          : {
              billing_account_id: form.billing_account_id,
              workspace_name: form.workspace_name,
            };
      await adminAPI.provisionAccount(attrs);
      setMsg({ type: "success", text: "Account provisioned" });
      setForm({ owner_name: "", owner_email: "", workspace_name: "", plan_id: "", billing_account_id: "" });
      onChange?.();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.error || "Provisioning failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "block w-full rounded-lg border-input shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  return (
    <div className="bg-card rounded-lg border border-border p-4 mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Provision an account</h3>
          <p className="text-sm text-muted-foreground mt-1">Manually onboard a customer who didn't self-signup.</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-4 py-2 text-sm font-medium rounded-lg text-foreground bg-card border border-input hover:bg-muted/50"
        >
          {open ? "Close" : "New"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="flex gap-2">
            {[
              { v: "new", label: "New account + owner" },
              { v: "existing", label: "Workspace in existing account" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setMode(t.v)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${
                  mode === t.v
                    ? "bg-black text-white border-black"
                    : "bg-card text-foreground border-input hover:bg-muted/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {msg && (
            <p className={`text-sm ${msg.type === "error" ? "text-red-600" : "text-green-700"}`}>{msg.text}</p>
          )}

          {mode === "new" ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Owner name</label>
                <input className={inputCls} value={form.owner_name} onChange={set("owner_name")} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Owner email</label>
                <input type="email" className={inputCls} value={form.owner_email} onChange={set("owner_email")} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Workspace name</label>
                <input className={inputCls} value={form.workspace_name} onChange={set("workspace_name")} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Plan</label>
                <select className={inputCls} value={form.plan_id} onChange={set("plan_id")} required>
                  <option value="">Select a plan…</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Billing account</label>
                <select className={inputCls} value={form.billing_account_id} onChange={set("billing_account_id")} required>
                  <option value="">Select an account…</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.owner?.name || a.owner?.email || `Account #${a.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Workspace name</label>
                <input className={inputCls} value={form.workspace_name} onChange={set("workspace_name")} required />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 text-sm font-medium rounded-lg text-white bg-black hover:bg-gray-800 disabled:opacity-50"
            >
              {submitting ? "Provisioning…" : "Provision"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
